import moment from 'moment';
import { captableUrl, documentGenerationJobsUrl, financialUrl } from 'common/config/urls';
import { FIRM_WATERFALLS_SLUG } from 'common/constants/firms';
import { FIRM_WATERFALL } from 'common/constants/user';
import { NOTIFICATION_TYPE } from 'context/NotificationCenterContext';
import type { AppNotification, NotificationStatus } from 'context/NotificationCenterContext';
import { NOTIFICATION_VARIANT, type NotificationVariant } from './NotificationCenter.types';

/** Terminal `status` values map 1:1 to UI variants. `in_progress` is handled below via `job.eventType`. */
const NOTIFICATION_STATUS_TO_VARIANT: Partial<Record<NotificationStatus, NotificationVariant>> = {
  completed: NOTIFICATION_VARIANT.COMPLETED,
  failed: NOTIFICATION_VARIANT.FAILED,
};

/** Maps server notification state to UI variant (toast + panel card). */
export function getNotificationVariant(notification: AppNotification): NotificationVariant {
  if (notification.type === NOTIFICATION_TYPE.GLOBAL) return NOTIFICATION_VARIANT.GLOBAL;
  if (notification.type === NOTIFICATION_TYPE.GENERAL) return NOTIFICATION_VARIANT.GENERAL;
  if (notification.type === NOTIFICATION_TYPE.FULFILLMENT) return NOTIFICATION_VARIANT.GENERAL;
  if (notification.type === NOTIFICATION_TYPE.WORKBOARD_TASK) return NOTIFICATION_VARIANT.GENERAL;
  const { status, job } = notification;
  const fromStatus = NOTIFICATION_STATUS_TO_VARIANT[status];
  if (fromStatus !== undefined) {
    return fromStatus;
  }
  if (job.eventType?.endsWith('.started')) {
    return NOTIFICATION_VARIANT.STARTED;
  }
  if (job.eventType?.endsWith('.processing')) {
    return NOTIFICATION_VARIANT.PROCESSING;
  }
  return NOTIFICATION_VARIANT.PROCESSING;
}

export interface NotificationLinks {
  /** Primary link — always a clickable target on the card. */
  primary: string;
  primaryLabel: string;
  /** Optional second link — used when the processing type warrants two destinations (e.g. CAP_TABLE). */
  secondary?: string;
  secondaryLabel?: string;
}

export function isValidUrl(url: string): boolean {
  try {
    return Boolean(new URL(url));
  } catch {
    return false;
  }
}

export function resolveTemplateUrl(
  template: string,
  ctx: Record<string, Record<string, string | undefined>>
): string | null {
  let allResolved = true;
  const result = template.replace(/\$\{(\w+)\.(\w+)\}/g, (_match, obj, prop) => {
    const val = ctx[obj]?.[prop];
    if (val == null) {
      allResolved = false;
      return _match;
    }
    return val;
  });
  return allResolved ? result : null;
}

const normalizeLegacyFirmWaterfallLink = (link?: string | null): string | null => {
  if (!link) {
    return null;
  }

  return link.replace(new RegExp(`(/firms/[^/?#]+/)${FIRM_WATERFALL}(?=([/?#]|$))`), `$1${FIRM_WATERFALLS_SLUG}`);
};

/**
 * Returns contextual deep-link(s) for a completed notification.
 *
 * - FINANCIAL_MAPPING → financials page (where the mapping result lands)
 * - CAP_TABLE         → two links: document generation jobs page + cap table page
 *                       (user must apply the job to the cap table before proceeding)
 * - default           → backend-provided `reservedLink`, or document generation jobs page
 */
export function getNotificationLinks(notification: AppNotification): NotificationLinks | null {
  if (
    notification.type === NOTIFICATION_TYPE.GLOBAL ||
    notification.type === NOTIFICATION_TYPE.GENERAL ||
    notification.type === NOTIFICATION_TYPE.FULFILLMENT ||
    notification.type === NOTIFICATION_TYPE.WORKBOARD_TASK
  ) {
    return null;
  }
  const { status, firmSlug, companySlug, job } = notification;
  if (status !== 'completed' || !firmSlug || !companySlug) {
    return null;
  }

  const processingType = job.processingType?.toUpperCase();

  if (processingType === 'FINANCIAL_MAPPING') {
    return {
      primary: financialUrl(firmSlug, companySlug),
      primaryLabel: 'View Financials',
    };
  }

  if (processingType === 'CAP_TABLE') {
    return {
      primary: documentGenerationJobsUrl(firmSlug, companySlug),
      primaryLabel: 'View Job',
      secondary: captableUrl(firmSlug, companySlug),
      secondaryLabel: 'View Cap Table',
    };
  }

  // Prefer the backend-provided link; fall back to the DGJ page.
  const reservedLink = Array.isArray(job.reservedLink) ? job.reservedLink[0] : job.reservedLink;
  const fallback = normalizeLegacyFirmWaterfallLink(reservedLink) ?? documentGenerationJobsUrl(firmSlug, companySlug);
  return { primary: fallback, primaryLabel: 'View Results' };
}

/** @deprecated Use `getNotificationLinks` instead. */
export function getNotificationUrl(notification: AppNotification): string | null {
  if (
    notification.type === NOTIFICATION_TYPE.GLOBAL ||
    notification.type === NOTIFICATION_TYPE.GENERAL ||
    notification.type === NOTIFICATION_TYPE.FULFILLMENT ||
    notification.type === NOTIFICATION_TYPE.WORKBOARD_TASK
  ) {
    return null;
  }
  const links = getNotificationLinks(notification);
  return links?.primary ?? null;
}

/** Milliseconds used to sort the panel: job event time, else last update. */
export function getNotificationSortTimeMs(notification: AppNotification): number {
  if (
    notification.type === NOTIFICATION_TYPE.GLOBAL ||
    notification.type === NOTIFICATION_TYPE.GENERAL ||
    notification.type === NOTIFICATION_TYPE.FULFILLMENT ||
    notification.type === NOTIFICATION_TYPE.WORKBOARD_TASK
  ) {
    return Math.max(notification.updatedAt.getTime(), notification.addedAt.getTime());
  }
  const { job, updatedAt, addedAt } = notification;
  if (job.timestamp) {
    const t = new Date(job.timestamp).getTime();
    if (!Number.isNaN(t)) {
      return t;
    }
  }
  return Math.max(updatedAt.getTime(), addedAt.getTime());
}

/** Panel order: notification time only (newest first). Read state does not affect order. */
export function compareNotificationPanelOrder(a: AppNotification, b: AppNotification): number {
  return getNotificationSortTimeMs(b) - getNotificationSortTimeMs(a);
}

/** Relative time for notification timestamps (e.g. panel list).
 * Returns null when the input is missing/invalid or the event happened less than 60 s ago,
 * so callers can omit the time label entirely instead of showing "Now". */
export function formatNotificationRelativeTime(
  dateInput: Date | string | null | undefined,
  nowMs = Date.now()
): string | null {
  if (dateInput == null) {
    return null;
  }

  const ts = moment(dateInput);
  if (!ts.isValid()) {
    return null;
  }

  const diffSeconds = Math.max(0, moment(nowMs).diff(ts, 'seconds'));
  if (diffSeconds < 60) {
    return null;
  }

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) {
    return `${diffMinutes} min ago`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  }

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
}
